/**
 * L229-WEB — attachment picker helpers (pure, unit-tested).
 *
 * Client-side mirror of the server caps (spec §2): ≤3 attachments, each
 * ≤2 MB, images only. The server 400s on violation; we pre-validate so the
 * reporter gets an instant, friendly error instead of a failed submit.
 */
import { MAX_ATTACHMENTS, MAX_ATTACHMENT_BYTES } from './supportConstants';

export interface AttachmentCandidate {
  name: string;
  type: string;
  size: number;
}

/**
 * Validates adding `candidate` when `currentCount` attachments are already
 * picked. Returns null when OK, or a human-readable error string.
 */
export function attachmentError(
  candidate: AttachmentCandidate,
  currentCount: number,
): string | null {
  if (currentCount >= MAX_ATTACHMENTS) {
    return `Maximum ${MAX_ATTACHMENTS} attachments.`;
  }
  if (!candidate.type.startsWith('image/')) {
    return 'Only images can be attached.';
  }
  if (candidate.size > MAX_ATTACHMENT_BYTES) {
    return 'Each image must be 2 MB or smaller.';
  }
  return null;
}

/** Reads a File into a base64 data URL (the wire format for `attachments`). */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}
